import React, { useEffect } from "react";
import { Button, Col, Form, Input, Row, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";

const MAX_DEPENDANTS = 20;

/* Always returns an array so antd Upload never crashes */
const toFileList = (att) => {
  if (!att) return [];
  if (Array.isArray(att)) return att;
  if (att.name) {
    return [
      {
        uid: att.uniqueId || "-1",
        name: att.name,
        status: "done",
        uniqueId: att.uniqueId,
      },
    ];
  }
  return [];
};

/* "2" -> 2, "" -> 0, "abc" -> 0, capped at MAX_DEPENDANTS */
const toCount = (value) => {
  const n = parseInt(value, 10);
  if (isNaN(n) || n < 0) return 0;
  return Math.min(n, MAX_DEPENDANTS);
};

/* ---------- API -> form ---------- */
export const toFormDependants = (details) => {
  const names = Array.isArray(details)
    ? details
    : Array.isArray(details?.name)
      ? details.name
      : [];

  const att = Array.isArray(details) ? null : details?.attachment;

  return {
    name: names.map((d) => ({ name: d?.name || "" })),
    attachment: toFileList(att),
  };
};

/* ---------- form -> payload ---------- */
export const toPayloadDependants = (form) => {
  const value = form.getFieldValue("dependentdetails") || {};
  const count = toCount(form.getFieldValue("dependent"));

  const names = (value.name || [])
    .slice(0, count)
    .filter((d) => d?.name && d.name.trim() !== "")
    .map((d) => ({ name: d.name.trim() }));

  const file = count > 0 ? toFileList(value.attachment)[0] : null;

  return {
    name: names,
    attachment: file
      ? {
        name: file.name,
        uniqueId: file?.response?.uniqueId || file?.uniqueId,
      }
      : null,
  };
};

/* ---------- component ---------- */
export default function DependantFields({ form, clientId, userId, onChange }) {
  const dependentValue = Form.useWatch("dependent", form);
  const count = toCount(dependentValue);

  // Keep the names array the same length as the count (keeps typed values)
  useEffect(() => {
    const current = form.getFieldValue(["dependentdetails", "name"]) || [];
    if (current.length === count) return;

    const resized = Array.from({ length: count }, (_, i) => ({
      name: current[i]?.name || "",
    }));
    form.setFieldValue(["dependentdetails", "name"], resized);
  }, [count, form]);

  const makeUploadRequest = (index) => async ({ file, onSuccess, onError }) => {
    try {
      // 1. get a pre-signed S3 url
      const res = await CALL_API(
        `upload-identity/${clientId}/${userId}?endpoint=peopledocuments`,
        "post",
        { filename: file.name }
      );
      if (!res?.url) throw new Error("No upload url");

      // 2. PUT the file to S3
      const putRes = await fetch(res.url, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!putRes.ok) throw new Error("S3 upload failed");

      // 3. register the document against the employee
      const { code } = await CALL_API(
        `upload-identity/${clientId}/${userId}`,
        "post",
        {
          name: file.name,
          type: file.type,
          objectKey: res?.key || "",
          documentName: "identityinformation", // see note below
          documentType: `dependant${index + 1}`, // see note below
        }
      );
      if (code !== STATUS_CODE.SUCCESS) throw new Error("Register failed");

      // the S3 key is what we save against the dependant
      onSuccess({ uniqueId: res?.key, key: res?.key });
    } catch (err) {
      console.error("[dependant upload] error --->", err);
      message.error(`Failed to upload ${file.name}`);
      onError(err);
    }
  };

  const uploadToServer = makeUploadRequest();

  if (count === 0) return null;

  return (
    <div style={{ marginTop: 8 }}>
      {/* One name field per dependant: validation REQUIRED */}
      {Array.from({ length: count }, (_, index) => (
        <Row key={index} gutter={8}>
          <Col flex="auto">
            <Form.Item
              name={["dependentdetails", "name", index, "name"]}
              label={`Dependant ${index + 1} Name`}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Dependant name is required",
                },
              ]}
            >
              <Input
                placeholder="Dependant Name"
                maxLength={50}
                onChange={onChange}
              />
            </Form.Item>
          </Col>
        </Row>
      ))}

      {/* ONE common upload: NO validation */}
      <Form.Item
        label="Attachment"
        name={["dependentdetails", "attachment"]}
        valuePropName="fileList"
        getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList || [])}
        getValueProps={(value) => ({ fileList: toFileList(value) })}
        rules={[{ required: false }]}
      >
        <Upload maxCount={1} customRequest={uploadToServer} listType="text">
          <Button icon={<UploadOutlined />}>Upload File</Button>
        </Upload>
      </Form.Item>
    </div>
  );
}